import { addDays, addHours, format, getDay, parseISO } from 'date-fns';
import React from 'react';
import { useCallback } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { notify } from '../../components/Notification';
import { GET, POST } from '../../services/api';
import { resolveFileSrc } from '../../helpers/fileSrcResolver';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import { DrawerContent, GeneratedPlaylist, GenPlaylistItem } from './styles';
import { Heading2 } from '../../components/Typography';
import { MdClose } from 'react-icons/md';
import { Form, Input, Select } from '../../components/Form';

export const PlaylistGeneration = ({ contract }) => {
	const formRef = useRef(null);

	const [showDrawer, setShowDrawer] = useState(false);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [generatonMode, setGenerationMode] = useState(null);
	const [generatedPlaylist, setGeneratedPlaylist] = useState([]);

	const [isGenerating, setIsGenerating] = useState(false);
	const [isVerifyingPlaylists, setIsVerifyingPlaylists] = useState(false);

	const handleChangeStartDate = useCallback((e) => {
		if (!e.target.value) {
			return;
		}

		const selectedDate = addHours(new Date(e.target.value), 3);
		console.log({ selectedDate });
		const weekDay = getDay(selectedDate);

		if (isNaN(weekDay)) {
			return;
		}

		setStartDate(format(selectedDate, 'yyyy-MM-dd'));

		if (weekDay === 1) {
			let endDate = addDays(selectedDate, 6);
			endDate = format(endDate, 'yyyy-MM-dd');
			setEndDate(endDate);
		}
	}, []);

	const countPlaylistScriptLiveDays = useCallback((script) => {
		let count = 0;

		Object.values(script.week).forEach((day) => {
			if (day?.length) {
				count = count + 1;
			}
		});

		return count;
	}, []);

	const validateGeneration = useCallback(async () => {
		const TALK_ID = '5e95ebab3542581e548c6e13';

		if (!startDate || !endDate) {
			notify('error', 'Selecione a data de início (Segunda-Feira)');
			return false;
		}

		if (!generatonMode) {
			formRef.current.setFieldError('type', 'Informe o tipo');
			return false;
		}

		const day = getDay(parseISO(startDate));

		if (day !== 1) {
			notify('error', 'O início precisa ser uma Segunda-Feira');
			return false;
		}

		try {
			formRef.current.setErrors({});

			if (contract?.value?.radio?._id === TALK_ID) {
				let query = `page=0&limit=7&program=${contract?.value?.program?._id}`;

				setIsVerifyingPlaylists(true);

				for (let index = 0; index <= 7; index++) {
					query = `${query}&date=${format(addDays(parseISO(startDate), index), 'dd/MM/yyyy')}`;
				}

				const {
					data: { playlistScript },
				} = await GET(`/api/v2/playlist-scripts/${contract?.value?.program?._id}`);

				const {
					data: { total },
				} = await GET(`/api/v2/playlists?${query}`);

				setIsVerifyingPlaylists(false);

				if (total >= countPlaylistScriptLiveDays(playlistScript)) {
					notify('warn', 'As playlists dessa semana já foram geradas');
					return false;
				} else {
					return true;
				}
			}

			// Rádios diferentes da TALK não passam pela verificação acima;
			// liberar a geração explicitamente (antes retornava undefined e o submit morria calado)
			return true;
		} catch (error) {
			setIsVerifyingPlaylists(false);
			notify('warn', 'Erro ao verificar as playlists existentes, recarregue a página');
			return false;
		}
	}, [contract, countPlaylistScriptLiveDays, endDate, startDate, generatonMode]);

	const handleGeneratePlaylist = useCallback(
		async (formData) => {
			try {
				if (!(await validateGeneration())) {
					return;
				}

				if (!formData.start) {
					return formRef.current.setFieldError('start', 'Informe a data de início');
				}

				const start = format(parseISO(formData.start), 'yyyy-MM-dd');
				const end = format(parseISO(endDate), 'yyyy-MM-dd');
				const payload = {
					programId: contract?.value?.program?._id,
					week: [start, end],
					type: generatonMode,
				};

				formRef.current.setErrors({});
				setIsGenerating(true);

				const { data } = await POST('/api/v2/playlists/generate-playlist', payload);

				setGeneratedPlaylist(data);
				setIsGenerating(false);

				return notify('success', 'Playlists geradas com sucesso');
			} catch (error) {
				console.error(error);
				setShowDrawer(false);
				setIsGenerating(false);

				if (error?.response) {
					const { message } = error.response.data;
					return notify('error', message);
				}

				return notify('error', 'Houve um erro, tente novamente');
			}
		},
		[validateGeneration, endDate, contract, generatonMode]
	);

	return (
		<>
			<Button
				onClick={() => setShowDrawer(true)}
				disabled={!contract?.value?.playlistPrivilege}
				variant='outlined'>
				Gerar Playlist
			</Button>

			<Drawer
				visible={showDrawer}
				onClose={() => {
					setShowDrawer(false);
					setGenerationMode(null);
				}}
				lock={isGenerating}>
				<DrawerContent>
					<header>
						<Heading2>Gerar Playlist</Heading2>
						<button
							className={isGenerating ? 'disabled' : ''}
							onClick={() => {
								setShowDrawer(false);
								setGenerationMode(null);
							}}
							disabled={isGenerating}>
							<MdClose />
						</button>
					</header>

					<p>
						Selecione somente a data de início e o sistema se encarregará de calcular toda a semana
						e gerar corretamente as playlists de cada dia para você{' '}
						<span role='img' aria-label=':)'>
							😃
						</span>
						.
					</p>

					<Form ref={formRef} onSubmit={handleGeneratePlaylist}>
						<Input
							name='start'
							label='Data de início'
							type='date'
							size={4}
							onKeyDown={(e) => e.preventDefault()}
							onChange={handleChangeStartDate}
						/>

						<Select
							label='Tipo'
							name='type'
							size={4}
							options={[
								{ label: 'Padrão', value: 'full' },
								{ label: 'Programas lançamento', value: 'stepped' },
							]}
							onChange={({ value }) => setGenerationMode(value)}
						/>

						{startDate && endDate && (
							<div className='dates-interval'>
								<strong>{format(parseISO(startDate), 'dd/MM/yyyy')}</strong>
								<span>até</span>
								<strong>{format(parseISO(endDate), 'dd/MM/yyyy')}</strong>
							</div>
						)}

						<Button
							variant='outlined'
							color='success'
							size={4}
							loading={isVerifyingPlaylists || isGenerating}>
							Gerar Playlist
						</Button>
					</Form>

					{generatedPlaylist.length !== 0 && (
						<GeneratedPlaylist>
							<div className='title'>
								<span>Playlists Geradas</span>
								<div className='line'></div>
							</div>

							<ul>
								{generatedPlaylist.map((playlist, index) => {
									if (typeof playlist === 'string') {
										return (
											<GenPlaylistItem key={index} unactive>
												Programa não vai ao ar
											</GenPlaylistItem>
										);
									}

									if (playlist?.alreadyExists) {
										return (
											<GenPlaylistItem alreadyExists key={index}>
												A playlist do dia {playlist?.date} já havia sido gerada
											</GenPlaylistItem>
										);
									}

									return (
										<GenPlaylistItem key={index}>
											<a
												href={resolveFileSrc({
													customS3Bucket: process.env.REACT_APP_PLAYLISTS_BUCKET,
													fileName: playlist.filename,
												})}
												target='_blank'
												rel='noopener noreferrer'
												download>
												Playlist do dia {playlist?.date}
											</a>
										</GenPlaylistItem>
									);
								})}
							</ul>
						</GeneratedPlaylist>
					)}
				</DrawerContent>
			</Drawer>
		</>
	);
};
